import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/macro';
import { toast } from 'sonner';

import { OrganisationSettingsService } from '@/api';
import { useAuthContext } from '@/contexts/AuthContext';
import { setOrgTimezone } from '@/utils/dateUtils';

/**
 * The organisation's configured IANA timezone (`organisations.timezone`).
 *
 * The read is open to staff, not only admins: every rendered time depends on the
 * zone, so the whole app needs it, and only the write is admin-gated.
 */
export const orgTimezoneKeys = {
  all: ['organisationTimezone'] as const,
  detail: (orgId: string | null | undefined) => [...orgTimezoneKeys.all, { orgId }] as const,
};

export function useOrgTimezoneQuery() {
  const { orgId, isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery<string>({
    queryKey: orgTimezoneKeys.detail(orgId),
    queryFn: async () => {
      if (!orgId) throw new Error('No organisation ID available');
      const response = await OrganisationSettingsService.getApiV1OrganisationsTimezone(orgId);
      return response.timezone;
    },
    enabled: !!orgId && isSignedIn && isAuthReady,
    // The zone changes about once in an organisation's lifetime. A long stale
    // time keeps this off the wire without pinning it: the settings mutation
    // invalidates the key, so a change still lands immediately.
    staleTime: 24 * 60 * 60 * 1000,
  });
}

/**
 * Load the organisation's timezone and install it for the date helpers.
 *
 * Mounted once, near the root of the authenticated app. The helpers in
 * `utils/dateUtils` read it through `getOrgTimezone()` rather than importing a
 * constant, because it arrives after the first render — a captured constant
 * would freeze `Europe/Zurich` into every module that imported it.
 *
 * Before it resolves, and whenever the value is unusable, the helpers keep the
 * documented default. That is the same fail-closed rule the backend applies:
 * falling through to UTC would move every displayed time by an hour with
 * nothing on screen to say so.
 */
export function useOrgTimezone() {
  const query = useOrgTimezoneQuery();
  const timezone = query.data;

  useEffect(() => {
    if (timezone) {
      setOrgTimezone(timezone);
    }
  }, [timezone]);

  return query;
}

// `silent` suppresses the success toast for a caller that saves the zone as part
// of a larger save and reports once (OrganisationForm).
export function useUpdateOrgTimezone(options?: { silent?: boolean }) {
  const { orgId } = useAuth();
  const silent = options?.silent ?? false;
  const { i18n } = useLingui();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (timezone: string) => {
      if (!orgId) throw new Error('No organisation ID available');
      const response = await OrganisationSettingsService.putApiV1OrganisationsTimezone(orgId, {
        timezone,
      });
      return response.timezone;
    },
    onSuccess: (timezone) => {
      // Applied before the refetch lands so the times already on screen move
      // with the setting rather than one render later.
      setOrgTimezone(timezone);
      void queryClient.invalidateQueries({ queryKey: orgTimezoneKeys.all });
      // Every cached list carries times rendered in the old zone.
      void queryClient.invalidateQueries();
      if (!silent) {
        toast.success(
          i18n._(msg({ id: 'settings.timezone.toast.saved', message: 'Timezone saved' }))
        );
      }
    },
    onError: () => {
      toast.error(
        i18n._(
          msg({ id: 'settings.timezone.toast.saveFailed', message: 'Could not save the timezone' })
        )
      );
    },
  });
}
